import { DecimalPipe } from '@angular/common';
import { EventEmitter, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { interval, Observable, timer } from 'rxjs';
import { debounce, filter, map, shareReplay } from 'rxjs/operators';
import { AppConfig } from 'src/app/config/app-config';
import { AppConfigService } from 'src/app/config/app-config.service';
import { FuldmagtChoiceService } from 'src/app/projekter/fuldmagt-choice.service';
import { ActiveWindowService } from '../active-window/active-window.service';
import { AuthService } from '../auth/auth.service';
import { LocalStorageService } from '../localstorage/localstorage.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRenewalService {
  lastActiveDebounce = 60;

  lastActive$: Observable<Date>;
  tokenRenewalIn$: Observable<number>;

  tokenIsRenewing = new EventEmitter<void>();
  tokenHasBeenRenewed = new EventEmitter<void>();

  tokenExpiresIn$: Observable<number>;
  tokenExpiresInString$: Observable<string>;

  private appConfig: AppConfig;

  constructor(
    private authService: AuthService,
    private router: Router,
    private localStorage: LocalStorageService,
    private decimalPipe: DecimalPipe,
    private activeWindowService: ActiveWindowService,
    private fuldmagtChoiceService: FuldmagtChoiceService,
    appConfigService: AppConfigService
  ) {
    this.appConfig = appConfigService.getConfig();

    this.lastActive$ = localStorage
      .select<string>('lastActive', new Date(), 1000)
      .pipe(map(lastActiveString => new Date(parseInt(lastActiveString, 10))));

    // Every time lastActive is emitted, emit duration before token should renew
    this.tokenRenewalIn$ = this.lastActive$.pipe(
      filter(() => !!this.authService.user),
      map(lastActive => this.authService.user.expires_at - lastActive.getTime() / 1000),
      map(tokenExpiryIn => tokenExpiryIn - this.appConfig.accessTokenExpiringNotificationTime),
      map(tokenExpiryWarningIn =>
        Math.min(
          this.lastActiveDebounce,
          tokenExpiryWarningIn - this.appConfig.silentRequestTimeout
        )
      ),
      shareReplay()
    );

    // Debounce request and then renew token
    this.tokenRenewalIn$.pipe(debounce(renewedIn => timer(renewedIn * 1000))).subscribe(() => {
      this.activeWindowService.isMainWindow().then(isMainWindow => {
        if (isMainWindow) {
          this.tokenIsRenewing.emit();
          this.authService
            .loginSilent()
            .then(() => {
              this.tokenHasBeenRenewed.emit();
              this.fuldmagtChoiceService.renew();
            })
            .catch(err => {
              console.error(err);
            });
        }
      });
    });

    this.tokenExpiresIn$ = interval(1000).pipe(
      map(() => (this.authService.user ? Math.max(this.authService.user.expires_in, 0) : -1))
    );

    this.tokenExpiresInString$ = this.tokenExpiresIn$.pipe(
      map(durationSeconds => {
        const durationMinutes = Math.floor(durationSeconds / 60);

        if (durationMinutes > 0) {
          return `${this.decimalPipe.transform(durationMinutes, '1.0-0')} ${
            durationMinutes !== 1 ? 'minutter' : 'minut'
          }`;
        } else {
          return `${this.decimalPipe.transform(durationSeconds, '1.0-0')} ${
            durationSeconds !== 1 ? 'sekunder' : 'sekund'
          }`;
        }
      })
    );

    // Mark user as active on each page navigation
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.setActive();
      }
    });
  }

  setActive() {
    this.localStorage.set('lastActive', new Date().getTime());
  }
}
